import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useData } from "@/hooks/useData";
import { useLanguage } from "../contexts/LanguageContext";
import companyService from "@/services/company/companyService";
import { storageService } from "@/services/storage/storageService";
import { useCompanyFormValidation } from "@/hooks/companies/useCompanyFormValidation";
import { Company, CompanyAccountState } from "@/types/company/company.types";
import { IPMAStation } from "@/types/ipma-station/ipma-station.types";
import { useNavigation } from "@/contexts/NavigationContext";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/auth/authService";

// Custom hooks
import { useLogoManagement } from "@/hooks/companies/useLogoManagement";
import { useCompanyForm } from "@/hooks/companies/useCompanyForm";

// Component imports
import { LogoSection } from "@/components/companies/LogoSection";
import { CompanyInfoSection } from "@/components/companies/CompanyInfoSection";
import { AccountAccessSection } from "@/components/companies/AccountAccessSection";
import { FormButtons } from "@/components/companies/FormButtons";

export interface FormData {
  name: string;
  nif: string;
  address: string;
  calculationManager: string;
  calculationManagerEmail: string;
  associateNumber: number | string;
  activityType: string;
  basinReference: string;
  weatherStationReference: string;
  basinName?: string;
  weatherStationName?: string;
  // Mixed activity company fields
  extractionBasinReference?: string;
  extractionWeatherStationReference?: string;
  extractionBasinName?: string;
  extractionWeatherStationName?: string;
  transformationBasinReference?: string;
  transformationWeatherStationReference?: string;
  transformationBasinName?: string;
  transformationWeatherStationName?: string;
  logoUrl?: string;
  accountState: CompanyAccountState;
}

export const CompanyForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user, claims } = useAuth();
  const { goBack } = useNavigation();

  // Ref to track if user has manually selected a logo
  const userHasSelectedLogo = useRef(false);
  // Ref to track if form submission is complete to prevent logo override
  const formSubmissionComplete = useRef(false);

  // Data fetching hooks
  const { data: companies, loading: companiesLoading } = useData<Company>({
    collectionName: "companies",
  });
  const { data: basins, loading: basinsLoading } = useData<{ id: string; name: string }>({
    collectionName: "basins",
  });
  const { data: ipmaStations = [], loading: ipmaStationsLoading } = useData<IPMAStation>({
    collectionName: "setup_ipmaStations",
  });

  // Fetch users collection for email uniqueness validation
  const { data: users = [], loading: usersLoading } = useData<{ id: string; email: string }>({
    collectionName: "users",
  });

  // Custom hooks for state management
  const { logoFile, logoPreview, handleLogoChange, handleDrop, handleRemoveLogo, updateLogoPreview, hasLogoChanged, setInitialLogoUrl } =
    useLogoManagement();

  const {
    formData,
    handleInputChange,
    handleNumberChange,
    handleSelectChange: handleSelectChangeBase,
    handleAddressChange,
    updateFormData,
    updateAccountState,
  } = useCompanyForm();

  // Wrap the handleSelectChange to include IPMA stations
  const handleSelectChange = (fieldName: string, value: string) => {
    handleSelectChangeBase(fieldName, value);

    // Handle legacy basin/station fields for single activity companies
    if (fieldName === "weatherStationReference" && value) {
      const selectedStation = ipmaStations.find((station) => station.id === value);
      if (selectedStation) {
        updateFormData({
          ...formData,
          weatherStationReference: value,
          weatherStationName: selectedStation.name,
        });
      }
    }

    // If changing basin, update the basinName and clear weather station
    if (fieldName === "basinReference" && value) {
      const selectedBasin = basins.find((basin) => basin.id === value);
      if (selectedBasin) {
        updateFormData({
          ...formData,
          basinReference: value, // The value is the basin ID
          basinName: selectedBasin.name,
          // Clear weather station when basin changes
          weatherStationReference: "",
          weatherStationName: "",
        });
        clearFieldValidation("weatherStationReference");
        clearFieldValidation("weatherStationName");
      }
    }

    // Handle extraction basin/station fields for mixed activity companies
    if (fieldName === "extractionWeatherStationReference" && value) {
      const selectedStation = ipmaStations.find((station) => station.id === value);
      if (selectedStation) {
        updateFormData({
          ...formData,
          extractionWeatherStationReference: value,
          extractionWeatherStationName: selectedStation.name,
        });
      }
    }

    if (fieldName === "extractionBasinReference" && value) {
      const selectedBasin = basins.find((basin) => basin.id === value);
      if (selectedBasin) {
        updateFormData({
          ...formData,
          extractionBasinReference: value,
          extractionBasinName: selectedBasin.name,
          extractionWeatherStationReference: "",
          extractionWeatherStationName: "",
        });
        clearFieldValidation("extractionWeatherStationReference");
        clearFieldValidation("extractionWeatherStationName");
      }
    }

    // Handle transformation basin/station fields for mixed activity companies
    if (fieldName === "transformationWeatherStationReference" && value) {
      const selectedStation = ipmaStations.find((station) => station.id === value);
      if (selectedStation) {
        updateFormData({
          ...formData,
          transformationWeatherStationReference: value,
          transformationWeatherStationName: selectedStation.name,
        });
      }
    }

    if (fieldName === "transformationBasinReference" && value) {
      const selectedBasin = basins.find((basin) => basin.id === value);
      if (selectedBasin) {
        updateFormData({
          ...formData,
          transformationBasinReference: value,
          transformationBasinName: selectedBasin.name,
          transformationWeatherStationReference: "",
          transformationWeatherStationName: "",
        });
        clearFieldValidation("transformationWeatherStationReference");
        clearFieldValidation("transformationWeatherStationName");
      }
    }
  };

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);

  // Store original company data for validation comparison
  const [originalCompany, setOriginalCompany] = useState<Company | null>(null);

  const isEditMode = !!id;

  // Form validation with email uniqueness check and company duplicate validation
  const { validation, handleInputBlur, isFormValid, touchAllFields, clearFieldValidation } = useCompanyFormValidation(
    formData,
    users,
    companies.filter(company =>
      company.name &&
      company.nif &&
      typeof company.associateNumber === 'number'
    ).map(company => ({
      id: company.id,
      name: company.name,
      nif: company.nif,
      associateNumber: company.associateNumber as number
    })),
    isEditMode ? id : undefined,
    originalCompany?.accountState,  // Pass original account state for comparison
    isSubmitted,
    usersLoading
  );

  // Check if any field has been touched and is valid, OR if logo has changed
  const hasChanges = useMemo(() => {
    if (!isEditMode) return true;

    // Check if any validation field has been touched and is valid
    const hasFieldChanges = Object.values(validation).some(
      field => field.touched && field.isValid
    );

    // Check if logo has changed
    const hasLogoChange = hasLogoChanged();

    return hasFieldChanges || hasLogoChange;
  }, [validation, isEditMode, hasLogoChanged]);

  // Helper function to determine redirect path based on user role
  const getRedirectPath = (companyId?: string) => {
    // For company role users, redirect to their main dashboard (root)
    if (claims?.role === "company") {
      return "/";
    }
    // For assimagra, get2c (admin), or other administrative roles, redirect to company dashboard
    if (claims?.role === "assimagra" || claims?.role === "get2c" || claims?.role === "admin") {
      return companyId ? `/companies/${companyId}/dashboard` : "/companies";
    }
    // Default fallback
    return "/companies";
  };

  // Initialize form data on component mount
  useEffect(() => {
    if (!isEditMode) {
      updateFormData({
        ...formData,
        accountState: {
          email: "",
          hasAssociatedAccount: false,
          isRegistered: false,
        },
      });
      setInitialLogoUrl("");
      setFormInitialized(true);
      return;
    }

    if (companiesLoading) {
      return;
    }

    const company = companies.find((c) => c.id === id);
    if (company) {
      // Store original company data for validation comparison
      setOriginalCompany(company);

      // Map legacy reference fields to their new counterparts if needed
      let basinReference = company.basinReference || "";
      if (basinReference && !basins.find(basin => basin.id === basinReference)) {
        // If basinReference is not a valid ID, try to find the basin by name
        const basinByName = basins.find(basin => basin.name === basinReference);
        if (basinByName) {
          basinReference = basinByName.id;
        }
      }
      const weatherStationReference = company.weatherStationReference || "";

      // Check if the current basin has any IPMA stations
      const hasStationsForBasin = basinReference && ipmaStations.some((station) => 
        !station.isArchived && station.basinId === basinReference
      );

      // Update formData with values from the company
      updateFormData({
        name: company.name || "",
        nif: company.nif || "",
        address: company.address || "",
        calculationManager: company.calculationManager || "",
        calculationManagerEmail: company.calculationManagerEmail || "",
        associateNumber: typeof company.associateNumber === "number" ? company.associateNumber : 0,
        activityType: company.activityType || "",
        basinReference: hasStationsForBasin ? basinReference : "",
        weatherStationReference: hasStationsForBasin ? weatherStationReference : "",
        // Mixed activity company fields
        extractionBasinReference: company.extractionBasinReference || "",
        extractionWeatherStationReference: company.extractionWeatherStationReference || "",
        extractionBasinName: company.extractionBasinName || "",
        extractionWeatherStationName: company.extractionWeatherStationName || "",
        transformationBasinReference: company.transformationBasinReference || "",
        transformationWeatherStationReference: company.transformationWeatherStationReference || "",
        transformationBasinName: company.transformationBasinName || "",
        transformationWeatherStationName: company.transformationWeatherStationName || "",
        logoUrl: company.logoUrl || "",
        accountState: company.accountState,
      });

      // Only update the logo preview if the user hasn't manually selected a logo and form submission isn't complete
      if (company.logoUrl && !userHasSelectedLogo.current && !formSubmissionComplete.current) {
        updateLogoPreview(company.logoUrl);
        setInitialLogoUrl(company.logoUrl);
      }

      setFormInitialized(true);
    } else {
      toast({
        variant: "destructive",
        title: t("companyForm.toasts.notFound.title"),
        description: t("companyForm.toasts.notFound.description"),
      });
      navigate(getRedirectPath());
    }
  }, [id, isEditMode, companies, companiesLoading, navigate, toast, t, updateFormData, updateLogoPreview, claims]);

  // Wrap the logo change handlers to track user interaction
  const handleLogoChangeWrapper = (file: File) => {
    userHasSelectedLogo.current = true;
    handleLogoChange(file);
  };

  const handleDropWrapper = (file: File) => {
    userHasSelectedLogo.current = true;
    handleDrop(file);
  };

  const handleRemoveLogoWrapper = () => {
    userHasSelectedLogo.current = true;
    handleRemoveLogo();
  };

  // Handler for creating/resending account invitations
  const handleCreateAccount = async () => {
    if (!formData.accountState.email || formData.accountState.email.trim() === "") {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("companyForm.invitation.emailRequired"),
      });
      return;
    }

    // Use the same logic as the form submission - save company and send invitation
    const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
    await handleSubmit(fakeEvent, true);
  };

  const handleChangeEmail = async (newEmail: string) => {
    if (!id) return;

    // Prevent changing to the same email
    if (newEmail === formData.accountState.email) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("companyForm.email.sameEmailError"),
      });
      return;
    }

    try {
      setIsSendingInvitation(true);

      // Track the invitation superseding in frontend storage
      const oldEmail = formData.accountState.email;
      if (oldEmail && oldEmail !== newEmail) {
        try {
          // Also try to invalidate the old invitation via backend (if available)
          await authService.invalidateSpecificInvitation(id, oldEmail, newEmail);
        } catch (invalidateError) {
          console.warn("Warning: Could not invalidate old invitation:", invalidateError);
          // Continue with email change even if invalidation fails
        }
      }

      await authService.updateCompanyEmail(id, newEmail);

      updateAccountState({
        email: newEmail,
        hasAssociatedAccount: true,
        isRegistered: false,
      });

      await companyService.edit(
        id,
        {
          accountState: {
            email: newEmail,
            hasAssociatedAccount: true,
            isRegistered: false,
          },
        },
        user?.uid
      );

      // Send invitation email after updating email
      await authService.sendAccountInvitation({
        email: newEmail,
        companyId: id,
        companyName: formData.name,
        role: "company",
      });

      toast({
        title: t("success"),
        description: t("companyForm.email.changeSuccess"),
      });
    } catch (error: any) {
      console.error("Error changing company email:", error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: error?.message || t("companyForm.email.changeError"),
      });
      throw error;
    } finally {
      setIsSendingInvitation(false);
    }
  };

  // Handler for password changes (company users only)
  const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
    try {
      setIsSubmitting(true);
      await authService.changePassword(currentPassword, newPassword);

      toast({
        title: t("success"),
        description: t("companyForm.password.changeSuccess"),
      });
      return true;
    } catch (error: any) {
      console.error("Password change error:", error);

      // Check if it's a password incorrect error (which will now have a consistent message)
      if (error?.message === "Current password is incorrect") {
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("validation.company.password.incorrectCurrent"),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("companyForm.password.changeError"),
        });
      }

      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent, forceSendInvitation = false) => {
    e.preventDefault();
    touchAllFields();

    if (!isFormValid) {
      return;
    }

    if (!user?.uid) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("auth.notAuthenticated"),
      });
      return;
    }

    setIsSubmitted(true);
    setIsSubmitting(true);

    try {
      let companyId: string;
      let logoUrl: string | undefined;

      // Upload logo first if there's a new logo file
      if (logoFile) {
        setIsUploadingLogo(true);
        try {
          // For create operations, generate a temporary ID for the logo path
          const tempCompanyId = isEditMode ? id! : `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          logoUrl = await storageService.uploadLogo(logoFile, tempCompanyId);
        } catch (uploadError) {
          console.error("Error uploading logo:", uploadError);
          toast({
            variant: "destructive",
            title: t("companyForm.toasts.logoUploadError.title"),
            description: t("companyForm.toasts.logoUploadError.description"),
          });
          setIsUploadingLogo(false);
          setIsSubmitting(false);
          return; // Stop the form submission if logo upload fails
        } finally {
          setIsUploadingLogo(false);
        }
      }

      // Ensure associateNumber is a number
      const formDataToSubmit = {
        ...formData,
        associateNumber: formData.associateNumber === "" ? null : parseInt(formData.associateNumber.toString(), 10),
        logoUrl: "empty", // Always include logoUrl field, default to "empty" to prevent filtering
      };

      // Handle logoUrl assignment based on logo state
      if (logoUrl) {
        // New logo was uploaded
        formDataToSubmit.logoUrl = logoUrl;
      } else if (logoPreview && logoPreview.trim() !== "") {
        // Existing logo preview (keep existing logo)
        formDataToSubmit.logoUrl = logoPreview;
      }
      // If neither condition is met, logoUrl remains "empty"

      // Add basin and weather station names to the data
      if (formData.basinReference) {
        const selectedBasin = basins.find((basin) => basin.id === formData.basinReference);
        if (selectedBasin) {
          formDataToSubmit.basinReference = selectedBasin.id;
        }
      }

      if (formData.weatherStationReference) {
        const selectedStation = ipmaStations.find((station) => station.id === formData.weatherStationReference);
        if (selectedStation) {
          formDataToSubmit.weatherStationName = selectedStation.name;
        }
      }

      // Add mixed activity basin and weather station names
      if (formData.extractionBasinReference) {
        const selectedBasin = basins.find((basin) => basin.id === formData.extractionBasinReference);
        if (selectedBasin) {
          formDataToSubmit.extractionBasinName = selectedBasin.name;
        }
      }

      if (formData.extractionWeatherStationReference) {
        const selectedStation = ipmaStations.find((station) => station.id === formData.extractionWeatherStationReference);
        if (selectedStation) {
          formDataToSubmit.extractionWeatherStationName = selectedStation.name;
        }
      }

      if (formData.transformationBasinReference) {
        const selectedBasin = basins.find((basin) => basin.id === formData.transformationBasinReference);
        if (selectedBasin) {
          formDataToSubmit.transformationBasinName = selectedBasin.name;
        }
      }

      if (formData.transformationWeatherStationReference) {
        const selectedStation = ipmaStations.find((station) => station.id === formData.transformationWeatherStationReference);
        if (selectedStation) {
          formDataToSubmit.transformationWeatherStationName = selectedStation.name;
        }
      }

      if (isEditMode) {
        console.log("formDataToSubmit", formDataToSubmit);
        console.log("editing company...", formDataToSubmit);
        await companyService.edit(id!, formDataToSubmit, user.uid);
        companyId = id!;
        
        // Force sending invitation (explicit resend or it has changed from the original)
        const shouldSendInvitation = forceSendInvitation || (
          originalCompany && 
          formData.accountState.email && 
          formData.accountState.email.trim() !== "" &&
          formData.accountState.email !== originalCompany.accountState?.email
        );
          
        if (shouldSendInvitation) {
          try {
            setIsSendingInvitation(true);
            await authService.sendAccountInvitation({
              email: formData.accountState.email,
              companyId: companyId,
              companyName: formData.name,
              role: "company",
            });

            toast({
              title: t("success"),
              description: t("companyForm.invitation.success"),
            });
          } catch (inviteError) {
            console.error("Error sending invitation:", inviteError);
            toast({
              variant: "destructive",
              title: t("note"),
              description: t("companyForm.invitation.errorButCompanyCreated"),
            });
          } finally {
            setIsSendingInvitation(false);
          }
        }
      } else {
        console.log("submitting company...", formDataToSubmit);
        companyId = await companyService.create(formDataToSubmit, user.uid);

        // If we uploaded a logo with a temporary ID, we need to move it to the correct path
        if (logoFile && logoUrl) {
          try {
            // Upload the logo again with the correct company ID
            const finalLogoUrl = await storageService.uploadLogo(logoFile, companyId);
            // Update the company with the final logo URL if it's different
            if (finalLogoUrl !== logoUrl) {
              await companyService.edit(companyId, { logoUrl: finalLogoUrl }, user.uid);
            }
          } catch (logoMoveError) {
            console.error("Error moving logo to final location:", logoMoveError);
            // Logo was already included in creation, so this is not critical
          }
        }

        // If email is provided during company creation, send an invitation
        if (formData.accountState.email && formData.accountState.email.trim() !== "") {
          try {
            setIsSendingInvitation(true);
            await authService.sendAccountInvitation({
              email: formData.accountState.email,
              companyId: companyId,
              companyName: formData.name,
              role: "company",
            });

            toast({
              title: t("success"),
              description: t("companyForm.invitation.success"),
            });
          } catch (inviteError) {
            console.error("Error sending invitation:", inviteError);
            toast({
              variant: "destructive",
              title: t("note"),
              description: t("companyForm.invitation.errorButCompanyCreated"),
            });
          } finally {
            setIsSendingInvitation(false);
          }
        }
      }

      toast({
        title: isEditMode ? t("companyForm.toasts.updateSuccess.title") : t("companyForm.toasts.createSuccess.title"),
        description: isEditMode
          ? t("companyForm.toasts.updateSuccess.description")
          : t("companyForm.toasts.createSuccess.description"),
      });

      // Mark form submission as complete to prevent logo override
      formSubmissionComplete.current = true;

      navigate(getRedirectPath(companyId));
    } catch (error) {
      console.error("Error in company form submission:", error);
      toast({
        variant: "destructive",
        title: isEditMode ? t("companyForm.toasts.updateError.title") : t("companyForm.toasts.createError.title"),
        description: isEditMode
          ? t("companyForm.toasts.updateError.description")
          : t("companyForm.toasts.createError.description"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Button variant="ghost" onClick={goBack} className="mb-4">
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t("navigation.back")}
      </Button>
      <div className="max-w-[1800px] mx-auto px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? t("companyForm.title.edit") : t("companyForm.title.new")}
          </h1>
          <p className="mt-2 text-base text-gray-600">
            {isEditMode ? t("companyForm.subtitle.edit") : t("companyForm.subtitle.new")}
          </p>
          {isEditMode && (
            <p className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
              {t("dashboard.company.verificationNotice")}
            </p>
          )}
        </div>

        <div className="space-y-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Main Section with two columns: Logo and Company Information */}
            <div className="grid grid-cols-12 gap-8">
              {/* Logo Upload (Left Column) */}
              <LogoSection
                logoPreview={logoPreview}
                onLogoChange={handleLogoChangeWrapper}
                onDrop={handleDropWrapper}
                onRemove={handleRemoveLogoWrapper}
              />

              {/* Main Info (Right Column) */}
              <CompanyInfoSection
                formData={formData}
                validation={validation}
                formInitialized={formInitialized}
                basins={basins}
                ipmaStations={ipmaStations}
                basinsLoading={basinsLoading}
                ipmaStationsLoading={ipmaStationsLoading}
                handleInputChange={handleInputChange}
                handleNumberChange={handleNumberChange}
                handleSelectChange={handleSelectChange}
                handleAddressChange={handleAddressChange}
                handleInputBlur={handleInputBlur}
                readOnlyFields={
                  claims?.role === "company"
                    ? [
                        "nif",
                        // Only lock associateNumber if it was already saved in the database
                        ...(originalCompany?.associateNumber && originalCompany.associateNumber !== 0 ? ["associateNumber"] : [])
                      ]
                    : []
                }
              />
            </div>

            {/* Account Access Section (Full Width) */}
            <AccountAccessSection
              isEditMode={isEditMode}
              formData={formData}
              formInitialized={formInitialized}
              validation={validation}
              claims={claims}
              user={user}
              isSubmitting={isSubmitting}
              isSendingInvitation={isSendingInvitation}
              users={users}
              usersLoading={usersLoading}
              handleInputChange={handleInputChange}
              handleInputBlur={handleInputBlur}
              handleCreateAccount={handleCreateAccount}
              handleChangeEmail={handleChangeEmail}
              handlePasswordChange={handlePasswordChange}
            />

            {/* Form Buttons */}
            <FormButtons
              isEditMode={isEditMode}
              isFormValid={isFormValid}
              isSubmitting={isSubmitting}
              isUploadingLogo={isUploadingLogo}
              isSendingInvitation={isSendingInvitation}
              formInitialized={formInitialized}
              hasChanges={hasChanges}
              goBack={goBack}
            />
          </form>
        </div>
      </div>
    </div>
  );
};
